<script setup lang="ts">
import { computed } from 'vue'

import { PopoverMenu } from '@collector/shared-ui'
import { ParticipantLogo } from '@collector/shared-ui-domain'
import { IncidentId } from '@collector/sportsapi-client-legacy'
import { useParticipants } from '@desktop/views/Relation/composables'
import PocketIncidentButton from '@desktop/views/Relation/Sports/components/RelationView/Main/PocketIncidents/PocketIncidentButton.vue'
import {
  AddIncidentPayloadWithOptionalTime,
  useEventIncidentsQueue,
} from '@desktop/views/Relation/Sports/composables'

type Props = {
  incidentId: IncidentId
  title: string
  additionalOptions?: {
    name: string
    value: number
  }[]
}

const props = defineProps<Props>()

// composables
const { homeParticipant, awayParticipant } = useParticipants()
const eventIncidentsQueue = useEventIncidentsQueue()

// data
const defaultMenuOptions = computed(() => {
  const home = homeParticipant.value
  const away = awayParticipant.value
  if (!home || !away) return []
  return [
    {
      name: home.short_name,
      value: home.id,
    },
    {
      name: away.short_name,
      value: away.id,
    },
  ]
})
const contextMenuOptions = computed(() => [
  ...defaultMenuOptions.value,
  ...(props.additionalOptions || []),
])

// methods
function getIncidentsOptions(
  optionValue: number,
): AddIncidentPayloadWithOptionalTime {
  if (props.additionalOptions) {
    const additionalOption = props.additionalOptions.find(
      (option) => option.value === optionValue,
    )
    if (additionalOption) {
      return {
        incident_id: optionValue,
      }
    }
  }
  return {
    incident_id: props.incidentId,
    participant_team_id: optionValue,
  }
}

function sendIncident(optionValue: number): void {
  const payload = getIncidentsOptions(optionValue)
  eventIncidentsQueue.addIncident(payload, {
    shouldResetPossession: true,
  })
}
</script>

<template>
  <PopoverMenu
    :options="contextMenuOptions"
    :content="{
      align: 'start',
    }"
    @clickOption="sendIncident"
  >
    <template #trigger>
      <PocketIncidentButton
        :incidentId
        :sendIncident="false"
      >
        {{ title }}
      </PocketIncidentButton>
    </template>

    <template #option="{ option }">
      <div
        class="flex items-center gap-2"
        :class="{
          'text-current-team-home-2': option.value === homeParticipant?.id,
          'text-current-team-away-2': option.value === awayParticipant?.id,
        }"
      >
        <ParticipantLogo
          v-if="
            homeParticipant &&
            awayParticipant &&
            (option.value === homeParticipant.id ||
              option.value === awayParticipant.id)
          "
          :participant="
            option.value === homeParticipant.id
              ? homeParticipant
              : awayParticipant
          "
        />
        <div>
          {{ option.name }}
        </div>
      </div>
    </template>
  </PopoverMenu>
</template>
